import api from './api'
import { BACKEND_URL } from './config'

export type AccountProvider = 'google' | 'imap' | 'caldav'
export type AccountStatus = 'active' | 'auth_error' | 'revoked' | 'connecting'

export interface AccountSummary {
  id: number
  provider: AccountProvider
  email: string
  displayName: string | null
  status: AccountStatus
  isPrimary: boolean
  createdAt: string
  calendars?: Array<{ uid: string; name: string; selected: boolean }>
}

export interface ImapConnectArgs {
  email: string
  host: string
  port: number
  user: string
  password: string
  secure: 'tls' | 'starttls' | 'none'
}

// ─── Layer 4 types ─────────────────────────────────────────────────

export type MailFolder = 'inbox' | 'starred' | 'sent' | 'drafts' | 'spam' | 'trash'

export interface MailPersonRef {
  id: string
  full_name: string | null
  email: string | null
}

export interface MailAttachmentInfo {
  filename: string
  mimeType: string
  size: number
  partId: string
  attachmentId?: string
}

export interface MailMessage {
  id: string
  gmail_message_id: string
  sent_at: string
  direction: 'inbound' | 'outbound' | 'internal' | 'unknown'
  snippet: string | null
  label_ids_json: unknown
  has_attachments: boolean
  sender: MailPersonRef | null
  to: MailPersonRef[]
  cc: MailPersonRef[]
  bcc: MailPersonRef[]
  /** Plain-text body — best effort. Empty when only HTML is available. */
  body: string
  /**
   * Raw HTML body when the email carried a text/html part. Rendered
   * in a sandboxed iframe by MailHtmlBody — never inserted directly
   * into the DOM. Null for plain-text-only emails.
   */
  bodyHtml: string | null
  attachments: MailAttachmentInfo[]
  hasHtmlOnly: boolean
  /**
   * Map of `Content-ID` → part metadata for inline images. The body
   * HTML references these via `cid:<id>`; the iframe component
   * rewrites them onto the attachment download URL so the images
   * render inline. Keys are stripped of the surrounding angle brackets.
   */
  inlineCids: Record<string, { partId: string; attachmentId?: string; mimeType: string }>
  /** Mail message DB row id — used for attachment download URLs. */
  messageDbId: string
}

export interface MailThreadSummary {
  id: string
  subject: string | null
  snippet: string | null
  message_count: number
  unread_count: number
  last_message_at: string
  labels_json: unknown
  // AI-L1: per-thread extraction snapshot from MailExtraction (latest message).
  extraction?: { topic: string | null; urgency: string | null } | null
  // OSPROD-MAIL-USER-LABELS: user-defined labels currently assigned
  // to this thread. Server attaches these by joining
  // MailUserLabelAssignment at list time.
  // OSPROD-MAIL-PRIORITY: `priority` is non-null when the label is a
  // priority marker (tints the whole row by its colour). `rank`
  // orders them (higher = more urgent). Regular labels have it null.
  user_labels?: Array<{ id: string; name: string; color: string; priority?: { rank: number } | null }>
}

// User-defined label with auto-apply rule. Stored on our side (we
// don't push to Gmail). Rules object mirrors the server-side
// userLabelRules.ts schema.
export interface MailUserLabel {
  id: string
  name: string
  color: string
  rules: {
    operator: 'AND' | 'OR'
    conditions: Array<{
      field: 'from' | 'to' | 'subject' | 'body' | 'hasAttachment'
      op: 'contains' | 'equals' | 'domain' | 'startsWith' | 'endsWith' | 'isTrue'
      value: string
    }>
    // OSPROD-MAIL-PRIORITY: when present this label is a priority
    // marker (row-tint), not a regular filter label.
    priority?: { rank: number }
  }
  createdAt?: string
  updatedAt?: string
}
export type MailUserLabelInput = Pick<MailUserLabel, 'name' | 'color' | 'rules'>

// Snooze metadata surfaced on threads served by the snoozed folder
// endpoint. Inbox listings never carry it (they exclude snoozed
// rows entirely).
export interface MailThreadSnoozed extends MailThreadSummary {
  snoozedUntil: string | null
}

export interface MailThreadFull extends MailThreadSummary {}

export interface MailListResult {
  threads: MailThreadSummary[]
  nextCursor: string | null
}

export interface MailSignature {
  accountId: number
  html: string
  isDefault: boolean
  autoAppend: boolean
  updatedAt?: string
}
export type MailSignatureInput = Pick<MailSignature, 'accountId' | 'html' | 'isDefault' | 'autoAppend'>

export interface MailVacation {
  enabled: boolean
  subject: string
  bodyHtml: string
  startDate: string | null
  endDate: string | null
  contactsOnly: boolean
  lastSyncedAt: string | null
}
export type MailVacationInput = Omit<MailVacation, 'lastSyncedAt'>

export interface MailDraftBody {
  accountId: number
  to: string[]
  cc?: string[]
  bcc?: string[]
  subject: string
  bodyHtml?: string
  bodyText?: string
  inReplyToMessageId?: string
  // OSPROD-163: queued attachment refs. Bytes live on /uploads and are read
  // server-side at send time; only the send path embeds them (drafts ignore them).
  attachments?: Array<{ fileName: string; fileUrl: string; fileSize?: number; mimeType: string }>
}

// ─── Admin: connected accounts & sync health ───────────────────────
// Shape returned by GET /mail/admin/accounts (server: @os/mail
// server/lib/mailAdminAggregate.ts + server/routes/admin.ts). Read-only
// dashboard for the global "Mail" module admin section. All dates are
// ISO strings (or null where no event has happened yet).

export interface MailAdminKpis {
  total: number
  active: number
  error: number
  revoked: number
  watchExpiringSoon: number
  neverSynced: number
}

export interface MailAdminAccount {
  id: string
  owner: string
  ownerUserId: string
  email: string
  displayName: string | null
  provider: string
  status: string
  initialSyncDone: boolean
  lastSyncedAt: string | null
  watchExpiresAt: string | null
  lastError: string | null
  connectedAt: string
}

export interface MailAdminFailure {
  accountId: string
  accountEmail: string
  owner: string
  kind: string
  error: string | null
  startedAt: string
}

export interface MailAdminOverview {
  kpis: MailAdminKpis
  accounts: MailAdminAccount[]
  recentFailures: MailAdminFailure[]
}

export const mailApi = {
  // ── Accounts (L3) ──
  listAccounts: () =>
    api.get<{ accounts: AccountSummary[]; integrationStatus?: string }>('/mail/accounts'),

  // ── Admin (global "Mail" module section) ──
  // Read-only overview of every connected mail account across the
  // workspace + recent sync failures. Server gates this to admins.
  adminListAccounts: () =>
    api.get<MailAdminOverview>('/mail/admin/accounts'),
  imapConnect: (args: ImapConnectArgs) =>
    api.post<{ accountId: number }>('/mail/accounts/imap', args),
  // Disconnect a mail account. With { purge: true } the server revokes the
  // Google token AND permanently deletes the account's synced mail (threads,
  // messages, attachments, labels, drafts) from business_os_mail; without it,
  // the account is just marked revoked and data is kept.
  disconnectAccount: (accountId: number | string, opts?: { purge?: boolean }) =>
    api.delete<{ ok?: boolean; purged?: boolean }>(
      `/mail/accounts/${accountId}${opts?.purge ? '?purge=true' : ''}`,
    ),

  // ── Threads (L4) ──
  listThreads: (params: {
    folder: MailFolder
    label?: string
    userLabelId?: string
    q?: string
    cursor?: string
    limit?: number
    accountId?: number
    // Phase 7 operator filters — see searchOperators.ts for the
    // canonical shape; mirrored here so callers stay typed without
    // depending on a UI module.
    subject?: string
    hasAttachment?: boolean
    isUnread?: boolean
    isRead?: boolean
    isStarred?: boolean
    before?: string
    after?: string
    olderThan?: string
    newerThan?: string
  }) =>
    api.get<MailListResult>('/mail/threads', { params }),
  getThread: (id: string) =>
    api.get<{ thread: MailThreadFull; messages: MailMessage[] }>(`/mail/threads/${id}`),
  setRead: (id: string, read: boolean) =>
    api.post<{ ok: true }>(`/mail/threads/${id}/read`, { read }),
  setStar: (id: string, starred: boolean) =>
    api.post<{ ok: true }>(`/mail/threads/${id}/star`, { starred }),
  setLabels: (id: string, add: string[], remove: string[]) =>
    api.post<{ ok: true }>(`/mail/threads/${id}/labels`, { add, remove }),
  move: (id: string, toFolder: string) =>
    api.post<{ ok: true }>(`/mail/threads/${id}/move`, { toFolder }),
  deleteThread: (id: string) =>
    api.delete<void>(`/mail/threads/${id}`),

  // ── Send + drafts (L4) ──
  send: (body: MailDraftBody) =>
    api.post<{ messageId: number }>('/mail/messages', body),
  createDraft: (body: MailDraftBody) =>
    api.post<{ id: string; gmailDraftId: string | null }>('/mail/drafts', body),
  updateDraft: (draftId: string, body: MailDraftBody) =>
    api.patch<{ id: string; gmailDraftId: string | null }>(`/mail/drafts/${draftId}`, body),
  sendDraft: (draftId: string) =>
    api.post<{ messageId: number }>(`/mail/drafts/${draftId}/send`),
  deleteDraft: (draftId: string) =>
    api.delete<void>(`/mail/drafts/${draftId}`),
  // List the user's auto-saved compose drafts (local-first, may or
  // may not also exist as a gmail draft mirror). Backs the Drafts
  // folder in the dashboard.
  listDrafts: () =>
    api.get<{
      drafts: Array<{
        id: string
        accountId: string
        accountEmail: string
        to: string[]
        cc: string[]
        bcc: string[]
        subject: string
        bodyText: string
        bodyHtml: string | null
        threadId: string | null
        inReplyToMessageId: string | null
        updatedAt: string
      }>
    }>('/mail/drafts'),

  // ── Labels ──
  // userLabels added by the user-labels module — kept on the same
  // endpoint so the sidebar only needs one round-trip.
  listLabels: () =>
    api.get<{ labels: string[]; userLabels?: Array<{ id: string; name: string; color: string }> }>('/mail/labels'),

  // ── User labels (our side) ──
  listUserLabels: () =>
    api.get<{ labels: MailUserLabel[] }>('/mail/user-labels'),
  createUserLabel: (body: MailUserLabelInput) =>
    api.post<{ label: MailUserLabel }>('/mail/user-labels', body),
  updateUserLabel: (id: string, body: Partial<MailUserLabelInput>) =>
    api.patch<{ label: MailUserLabel }>(`/mail/user-labels/${id}`, body),
  deleteUserLabel: (id: string) =>
    api.delete<void>(`/mail/user-labels/${id}`),
  applyUserLabel: (id: string, threadId: string) =>
    api.post<{ ok: true }>(`/mail/user-labels/${id}/apply`, { threadId }),
  unapplyUserLabel: (id: string, threadId: string) =>
    api.delete<void>(`/mail/user-labels/${id}/apply`, { params: { threadId } }),

  // ── Saved searches (Phase 7) ──
  listSavedSearches: () =>
    api.get<{ savedSearches: Array<{ id: string; name: string; query: string; position: number; updatedAt?: string }> }>(
      '/mail/saved-searches',
    ),
  createSavedSearch: (name: string, query: string) =>
    api.post<{ savedSearch: { id: string; name: string; query: string; position: number } }>(
      '/mail/saved-searches',
      { name, query },
    ),
  deleteSavedSearch: (id: string) =>
    api.delete<void>(`/mail/saved-searches/${id}`),

  // ── Manual sync trigger — refresh button on the thread list ──
  // With no accountId: fans out across every active Google account
  // the user owns. With one: syncs that single account. Response
  // carries one row per account-touched so partial-failure can be
  // surfaced (e.g. "2 of 3 accounts refreshed"). `degraded:true`
  // means the Python endpoint isn't deployed yet — soft success.
  sync: (accountId?: number) =>
    api.post<{
      ok: boolean
      degraded?: boolean
      /** Set when the agent has no active Google accounts (IMAP-only / brand-new user). */
      reason?: 'no-google-accounts'
      accounts: Array<{
        accountId: number
        email: string
        ok: boolean
        started?: boolean
        error?: string
        code?: string
      }>
    }>(
      '/mail/sync',
      accountId ? { accountId } : {},
    ),

  // ── Attachments ──
  // URL is a GET endpoint hit via <a href>. We never read the bytes
  // through axios — the browser handles the response directly so
  // images can be referenced from <img src="…">.
  attachmentUrl: (messageDbId: string, partId: string, opts?: { download?: boolean }) => {
    const dl = opts?.download ? '?dl=1' : ''
    // BACKEND_URL is empty for browser/dev (relative path resolved
    // against the page origin) and set for the packaged Electron
    // build (file:// → no origin → needs the full host). The auth
    // cookie path used by api.ts doesn't help inline images, so
    // append the JWT as a query param when present — the route
    // accepts either Authorization header OR `?token=…` so img/href
    // requests authenticate without JS in the loop.
    const token = (() => { try { return localStorage.getItem('token') || '' } catch { return '' } })()
    const tokenPart = token ? `${dl ? '&' : '?'}token=${encodeURIComponent(token)}` : ''
    return `${BACKEND_URL}/api/mail/messages/${encodeURIComponent(messageDbId)}/attachments/${encodeURIComponent(partId)}${dl}${tokenPart}`
  },

  // ── Snooze (Phase 6) ──
  snooze: (threadId: string, remindAt: string) =>
    api.post<{ ok: true; remindAt: string }>(`/mail/snooze/${threadId}`, { remindAt }),
  unsnooze: (threadId: string) =>
    api.delete<void>(`/mail/snooze/${threadId}`),
  listSnoozed: () =>
    api.get<{ threads: MailThreadSnoozed[]; nextCursor: null }>('/mail/snooze/folder'),

  // ── Settings: signatures + vacation ──
  listSignatures: () =>
    api.get<{ signatures: MailSignature[] }>('/mail/settings/signatures'),
  saveSignatures: (signatures: MailSignatureInput[]) =>
    api.put<{ ok: true }>('/mail/settings/signatures', { signatures }),
  getVacation: () =>
    api.get<MailVacation>('/mail/settings/vacation'),
  saveVacation: (payload: MailVacationInput) =>
    api.put<{ ok: true; enabled: boolean; lastSyncedAt: string | null; syncError: string | null }>(
      '/mail/settings/vacation', payload,
    ),

  // ── Search (L6) ──
  searchMessages: (q: string, opts?: { from?: string; to?: string; limit?: number }) =>
    api.get<{
      messages: Array<{
        id: number
        threadId: number
        subject: string
        fromEmail: string
        fromName: string
        toEmails: string[]
        bodyText: string
        isRead: boolean
        hasAttachments: boolean
        sentAt: string
        receivedAt: string
      }>
      source: 'clickhouse' | 'mysql-fallback' | 'no-agent'
    }>('/mail/search', {
      params: { q, ...(opts?.from ? { from: opts.from } : {}), ...(opts?.to ? { to: opts.to } : {}), ...(opts?.limit ? { limit: opts.limit } : {}) },
    }),
}
